import {pickKeysLike} from '@aboutyou/utils';
import React, {PropsWithChildren} from 'react';
import {Link as RouterLink} from 'react-router-dom';
import {useInitialLocation} from './context';
import {createRouteState} from './useHistory';

export type RouterLinkTarget = Parameters<typeof RouterLink>[0]['to'];

interface Props {
  openExternalLinkInNewTab?: boolean;
  openLinkInNewTab?: boolean;
  considerScrollTopAnchor?: boolean;
  scrollTopAnchorId?: string;
  forcePushBehaviour?: boolean;
  [dataAttr: `data-${string}`]: string;
}

type OriginalRouterLinkProps = Parameters<RouterLink>[0];

type AllowedBaseLinkProps = Pick<
  OriginalRouterLinkProps,
  | 'to'
  | 'replace'
  | 'component'
  | 'target'
  | 'rel'
  | 'className'
  | 'style'
  | 'type'
  | 'title'
  | 'aria-label'
  | 'onClick'
  | 'onMouseEnter'
  | 'onTouchStart'
  | 'tabIndex'
>;

export type LinkProps = AllowedBaseLinkProps &
  PropsWithChildren<Props> & {ref?: React.Ref<HTMLAnchorElement>};

export const Link = ({
  to: originalTo,
  replace,
  openExternalLinkInNewTab = true,
  openLinkInNewTab,
  'data-testid': dataTestId = 'link',
  'aria-label': ariaLabel,
  considerScrollTopAnchor,
  scrollTopAnchorId,
  forcePushBehaviour,
  children,
  className,
  style,
  type,
  onClick,
  onMouseEnter,
  onTouchStart,
  title,
  target,
  component,
  rel,
  tabIndex,
  ref: forwardedRef,
  ...props
}: LinkProps) => {
  const dataAttrProps = pickKeysLike(props, 'data-');

  const unifiedTo = useUnifyLinkUrl(originalTo);
  const isExternalUrl =
    typeof unifiedTo === 'string' &&
    (isSpecial(unifiedTo) || isExternal(unifiedTo) || openLinkInNewTab);
  // on the server, if the url is a string, we don't care about state or click handlers
  // this is a pure cost reduction on the server
  if (!process.browser && process.env.NODE_ENV !== 'test') {
    // most urls are strings, therefore we only optimise for those
    if (typeof unifiedTo === 'string') {
      const Component = component || 'a';
      let targetProp = target;
      let relProp = rel;
      if (isExternalUrl) {
        targetProp = openExternalLinkInNewTab ? '_blank' : '_self';
        relProp = 'noreferrer';
      }

      return (
        <Component
          ref={forwardedRef}
          href={unifiedTo}
          data-testid={dataTestId}
          aria-label={ariaLabel}
          className={className}
          style={style}
          type={type}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onTouchStart={onTouchStart}
          title={title}
          target={targetProp}
          rel={relProp}
          tabIndex={tabIndex}
          {...dataAttrProps}
        >
          {children}
        </Component>
      );
    }
  }

  const state = createRouteState({
    considerScrollTopAnchor,
    scrollTopAnchorId,
    forcePushBehaviour,
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const to = useGetTo(unifiedTo, state);

  if (isExternalUrl) {
    return (
      <a
        ref={forwardedRef}
        href={unifiedTo}
        data-testid={dataTestId}
        aria-label={ariaLabel}
        className={className}
        style={style}
        type={type}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onTouchStart={onTouchStart}
        title={title}
        target={openExternalLinkInNewTab ? '_blank' : '_self'}
        rel="noreferrer noopener"
        tabIndex={tabIndex}
        {...dataAttrProps}
      >
        {children}
      </a>
    );
  }

  return (
    <RouterLink
      ref={forwardedRef}
      to={to}
      data-testid={dataTestId}
      replace={replace}
      component={component}
      aria-label={ariaLabel}
      className={className}
      style={style}
      type={type}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onTouchStart={onTouchStart}
      title={title}
      target={openLinkInNewTab ? '_blank' : target}
      rel={openLinkInNewTab ? 'noreferrer noopener' : rel}
      tabIndex={tabIndex}
      {...dataAttrProps}
    >
      {children}
    </RouterLink>
  );
};

const useHostname = () => {
  const {host, protocol} = useInitialLocation();
  return `${protocol}://${host}`;
};

const useUnifyLinkUrl = (to: Parameters<RouterLink>[0]['to']) => {
  const hostname = useHostname();
  if (typeof to !== 'string') {
    return to;
  }

  let nextTo = to;
  if (nextTo.startsWith(hostname)) {
    nextTo = nextTo.replace(hostname, '');
  }
  if (nextTo.startsWith('?')) {
    nextTo = `/${nextTo}`;
  }
  return nextTo;
};

const useGetTo = (
  originalTo: Parameters<RouterLink>[0]['to'],
  state?: object,
) => {
  const hostname = useHostname();
  if (!state) {
    return originalTo;
  }
  switch (typeof originalTo) {
    case 'function':
      return originalTo;
    case 'string': {
      const url = new URL(originalTo, hostname);
      return {
        pathname: url.pathname,
        search: url.search,
        hash: url.hash,
        state,
      };
    }
    default:
      return {
        ...originalTo,
        state: Object.assign(state, originalTo.state),
      };
  }
};

const specialPrefix = ['mailto:', 'tel:', 'appaboutyou:', 'intent:'];
export function isSpecial(href: string) {
  return (
    href[0] !== '/' && specialPrefix.some(prefix => href.startsWith(prefix))
  );
}

const externalPrefix = ['https:', 'http:', 'www.'];
export function isExternal(href: string) {
  return (
    href[0] !== '/' && externalPrefix.some(prefix => href.startsWith(prefix))
  );
}
